/**
 * TilePreviewPanel.jsx - Shows preview of a selected tile's details
 */

import React, { useState } from 'react';
import BuildingChip from '../../common/BuildingChip';
import ResourceChip from '../../common/ResourceChip';
import { DUNGEON_BIOMES } from '../../../data/world-map/dungeonBiomes';
import { DUNGEON_CONFIG, LOOT_TABLES, ENEMY_TEMPLATES } from '../../../data/dungeon';
import { WONDERS } from '../../../data/wonders';
import { GREAT_WORKS } from '../../../data/world-map/greatWorks';
import { SHRINE_BIOMES } from '../../../data/world-map/shrineBiomes';
import { resources } from '../../../data/resources';
import KingdomPreviewCard from './KingdomPreviewCard';

// Helper to get resource icon from canonical source
const getResourceIcon = (resourceId) => {
  return resources[resourceId]?.icon || '📦';
};

// Format effect values for display
const formatEffect = (value) => `+${Math.round(value * 100)}%`;

// Get human-readable shrine effect description
const getShrineEffectDescription = (shrineEffect) => {
  if (!shrineEffect) return '';
  switch (shrineEffect.type) {
    case 'stat':
      return `+${shrineEffect.value} ${shrineEffect.stat} permanently`;
    case 'health':
      return `+${shrineEffect.value} Max HP permanently`;
    case 'xp':
      return `${shrineEffect.multiplier}× your level in XP`;
    case 'random_blessing':
      return 'Random permanent blessing';
    default:
      return 'Unknown blessing';
  }
};

// Format seconds as "Xm Ys" or just "Xs"
const formatTime = (seconds) => {
  const secs = Math.ceil(seconds);
  if (secs >= 60) {
    const mins = Math.floor(secs / 60);
    const remainingSecs = secs % 60;
    return remainingSecs > 0 ? `${mins}m ${remainingSecs}s` : `${mins}m`;
  }
  return `${secs}s`;
};

// Can Build Section with expandable popup
function CanBuildSection({ availableBuildings, buildingCounts }) {
  const [showAllBuildings, setShowAllBuildings] = useState(false);
  const MAX_VISIBLE = 3;

  if (!availableBuildings || availableBuildings.length === 0) {
    return (
      <div className="wm-preview-section">
        <div className="wm-preview-label">Can Build</div>
        <div className="wm-preview-empty">None</div>
      </div>
    );
  }

  const visibleBuildings = availableBuildings.slice(0, MAX_VISIBLE);

  return (
    <div className="wm-preview-section">
      <div className="wm-preview-label">Can Build</div>
      <div className="wm-preview-builds">
        {visibleBuildings.map(bld => (
          <div key={bld.id} className="wm-preview-build-item">
            <BuildingChip id={bld.id} size="small" count={buildingCounts?.[bld.id] || 0} />
          </div>
        ))}
        {/* Always show "View All" button */}
        <button
          className="wm-preview-build-all-btn"
          onClick={() => setShowAllBuildings(true)}
          title={`View all ${availableBuildings.length} buildings for this biome`}
        >
          📋 View All ({availableBuildings.length})
        </button>
      </div>

      {/* Expandable popup showing all buildings */}
      {showAllBuildings && (
        <div className="wm-build-popup-overlay" onClick={() => setShowAllBuildings(false)}>
          <div className="wm-build-popup" onClick={e => e.stopPropagation()}>
            <div className="wm-build-popup-header">
              <span className="wm-build-popup-title">Available Buildings ({availableBuildings.length})</span>
              <button className="wm-build-popup-close" onClick={() => setShowAllBuildings(false)}>×</button>
            </div>
            <div className="wm-build-popup-content">
              {availableBuildings.map(bld => (
                <div key={bld.id} className="wm-build-popup-item">
                  <BuildingChip id={bld.id} size="small" count={buildingCounts?.[bld.id] || 0} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function TilePreviewPanel({
  previewTile,
  worldMap,
  BIOMES,
  buildings,
  buildingCounts,
  availableBuildings,
  calculateGatherYield,
  onClose,
  currentGreatWorkConstruction,
  greatWorkLocations,
  getGreatWorkProgressInfo,
  greatWorkRemaining, // Pass this to trigger re-renders every second
  // Trade Route props
  isOnTradeRoute,
  hasTradeRouteSegment,
  // Outpost props
  onCreateOutpost,
  onManageOutpost,
  getOutpostConstructionProgress,
  // Shrine props
  currentNodeId,
  onActivateShrine,
}) {
  if (!previewTile || !worldMap?.discoveredNodes?.[previewTile]) {
    return null;
  }

  const node = worldMap.discoveredNodes[previewTile];
  const biome = node.isHub ? null : BIOMES?.[node.biome];
  const tileBuildingId = worldMap.tileBuildings?.[previewTile];
  const tileBuilding = tileBuildingId ? buildings[tileBuildingId] : null;
  const yields = calculateGatherYield?.(previewTile);

  // Check if this tile has an outpost
  const outpost = worldMap.outposts?.[previewTile];
  const hasOutpost = !!outpost;
  const isOutpostConstructing = outpost?.isConstructing;
  const isOutpostComplete = hasOutpost && !isOutpostConstructing;

  // Check if this is a dungeon biome
  const isDungeonBiome = biome?.isDungeonBiome;
  const dungeonBiome = isDungeonBiome ? DUNGEON_BIOMES[node.biome] : null;
  const dungeonConfig = dungeonBiome ? DUNGEON_CONFIG?.TIERS?.find(t => t.id === dungeonBiome.dungeonTier) : null;

  // Check if this is an encounter biome
  const isEncounterBiome = biome?.isEncounterBiome;

  // Check if this is a tower biome
  const isTowerBiome = biome?.isTowerBiome;

  // Check if this is a shrine biome
  const isShrineBiome = biome?.isShrineBiome;
  const shrineBiome = isShrineBiome ? SHRINE_BIOMES[node.biome] : null;
  const isActivated = worldMap.activatedShrines?.[previewTile];
  const isPlayerAtShrine = currentNodeId === previewTile;

  // Check if this is a wonder site
  const isWonderSite = node.biome === 'wonderSite' || node.wonderSiteId;
  const wonderId = node.wonderSiteId;
  const wonder = wonderId ? WONDERS[wonderId] : null;

  // Check for Great Work construction or completed on this tile
  const isGreatWorkUnderConstruction = currentGreatWorkConstruction?.nodeId === previewTile;
  const builtGreatWorkId = greatWorkLocations ? Object.entries(greatWorkLocations).find(([wId, nId]) => nId === previewTile)?.[0] : null;
  const greatWorkInProgress = isGreatWorkUnderConstruction ? GREAT_WORKS[currentGreatWorkConstruction.workId] : null;
  const builtGreatWork = builtGreatWorkId ? GREAT_WORKS[builtGreatWorkId] : null;
  const progressInfo = isGreatWorkUnderConstruction ? getGreatWorkProgressInfo?.() : null;

  // Check if this is a kingdom/city-state biome
  const isKingdomBiome = biome?.isKingdomBiome;

  // Render kingdom/city-state preview
  if (isKingdomBiome) {
    return (
      <div className="wm-preview-panel">
        <div className="wm-preview-header">
          <span className="wm-preview-icon">{biome.icon}</span>
          <span className="wm-preview-name">{biome.name}</span>
          <button className="wm-preview-close" onClick={onClose}>x</button>
        </div>
        <KingdomPreviewCard kingdomId={node.biome} />
      </div>
    );
  }

  // Render shrine preview - early return for shrine biomes
  if (isShrineBiome && shrineBiome) {
    return (
      <div className={`wm-preview-panel wm-preview-shrine ${isActivated ? 'wm-shrine-activated' : ''}`}>
        <div className="wm-preview-header">
          <span className="wm-preview-icon">{shrineBiome.icon}</span>
          <span className="wm-preview-name">{shrineBiome.name}</span>
          <button className="wm-preview-close" onClick={onClose}>x</button>
        </div>

        <div className={`wm-shrine-badge ${isActivated ? 'activated' : ''}`}>
          {isActivated ? '✓ Blessed' : '🙏 Shrine'}
        </div>

        <p className="wm-preview-description wm-shrine-description">
          {shrineBiome.description}
        </p>

        <div className="wm-shrine-effect">
          <span className="wm-shrine-effect-label">Blessing:</span>
          <span className="wm-shrine-effect-value">
            {getShrineEffectDescription(shrineBiome.shrineEffect)}
          </span>
        </div>

        {!isActivated && isPlayerAtShrine && onActivateShrine && (
          <button
            className="wm-shrine-activate-btn"
            onClick={() => onActivateShrine(previewTile)}
          >
            🙏 Receive Blessing
          </button>
        )}

        {!isActivated && !isPlayerAtShrine && (
          <div className="wm-shrine-hint">
            Double-click to travel here and receive the blessing.
          </div>
        )}

        {isActivated && (
          <div className="wm-shrine-activated-info">
            <span className="wm-shrine-blessed-icon">✨</span>
            <span>Blessing received: {isActivated.bonusApplied}</span>
          </div>
        )}
      </div>
    );
  }

  // Render wonder site preview - always early return for wonder sites
  if (isWonderSite) {
    return (
      <div className="wm-preview-panel wm-preview-wonder">
        <div className="wm-preview-header">
          <span className="wm-preview-icon">{node.siteIcon || '🏛️'}</span>
          <span className="wm-preview-name">{node.siteName || 'Wonder Site'}</span>
          <button className="wm-preview-close" onClick={onClose}>x</button>
        </div>
        <div className="wm-preview-wonder-badge">Wonder Site</div>
        {wonder ? (
          <>
            <div className="wm-preview-hint wm-preview-wonder-hint">
              {wonder.lore}
            </div>

            <div className="wm-preview-section">
              <div className="wm-preview-label">Effects when built</div>
              <div className="wm-preview-wonder-effects">
                {wonder.effects.productionBonus && Object.entries(wonder.effects.productionBonus).map(([res, val]) => (
                  <span key={res} className="wm-wonder-effect bonus">
                    {getResourceIcon(res)} {res}: {formatEffect(val)}
                  </span>
                ))}
                {wonder.effects.globalProductionBonus && (
                  <span className="wm-wonder-effect global">
                    🌍 All: {formatEffect(wonder.effects.globalProductionBonus)}
                  </span>
                )}
                {wonder.effects.researchSpeedBonus && (
                  <span className="wm-wonder-effect research">
                    📖 Research: {formatEffect(wonder.effects.researchSpeedBonus)}
                  </span>
                )}
                {wonder.effects.flatBonus && Object.entries(wonder.effects.flatBonus).map(([res, val]) => (
                  <span key={res} className="wm-wonder-effect flat">
                    {getResourceIcon(res)} +{val}/s
                  </span>
                ))}
              </div>
            </div>

            <div className="wm-preview-wonder-footer">
              <span className="wm-preview-wonder-tier">Tier {wonder.tier}</span>
              <span className="wm-preview-wonder-cta">Check Wonders Panel to build!</span>
            </div>
          </>
        ) : (
          <div className="wm-preview-hint wm-preview-wonder-hint">
            Ancient wonder site. Check Monuments Panel to build!
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="wm-preview-panel">
      <div className="wm-preview-header">
        <span className="wm-preview-icon">
          {hasOutpost ? '🏰' : node.isHub ? '🏰' : biome?.icon || '?'}
        </span>
        <span className={`wm-preview-name ${hasOutpost ? 'outpost' : ''}`}>
          {hasOutpost ? outpost.name : node.isHub ? 'Kingdom' : biome?.name || '?'}
        </span>
        <button className="wm-preview-close" onClick={onClose}>x</button>
      </div>
      <div className="wm-preview-hint">
        {hasOutpost
          ? isOutpostConstructing
            ? 'Outpost under construction. Double-click to travel here.'
            : 'Your outpost settlement. Double-click to travel here.'
          : node.isHub
            ? 'Your kingdom stronghold. Double-click to teleport home.'
            : biome?.isTeleportBeacon
              ? 'Teleport beacon. Double-click to instantly travel here from anywhere.'
              : isDungeonBiome
                ? 'Dungeon entrance. Double-click to move here and enter.'
                : biome?.description || 'Click adjacent tiles to explore. Double-click discovered tiles to travel.'}
      </div>

      {/* Dungeon Preview - Compact Grid Layout */}
      {isDungeonBiome && dungeonBiome && dungeonConfig && (
        <div className="wm-dungeon-preview-grid">
          {/* Top Row: Stats */}
          <div className="wm-dungeon-stat-compact">
            <span className="wm-dungeon-stat-label">Tier</span>
            <span className="wm-dungeon-stat-value">{dungeonConfig.name}</span>
          </div>
          <div className="wm-dungeon-stat-compact">
            <span className="wm-dungeon-stat-label">Level</span>
            <span className="wm-dungeon-stat-value">
              {dungeonConfig.baseEnemyLevel}
            </span>
          </div>
          <div className="wm-dungeon-stat-compact">
            <span className="wm-dungeon-stat-label">Reward</span>
            <span className="wm-dungeon-stat-value">+{dungeonConfig.expReward}</span>
          </div>

          {/* Bottom Row: Enemies & Loot */}
          <div className="wm-dungeon-enemies-compact">
            <span className="wm-dungeon-compact-label">Enemies</span>
            <div className="wm-dungeon-mob-icons">
              {dungeonBiome.dungeonMobPool.slice(0, 5).map((mobId, idx) => {
                const mob = ENEMY_TEMPLATES[mobId];
                return (
                  <span key={idx} className="wm-dungeon-mob-icon" title={mob?.name || mobId}>
                    {mob?.icon || '👹'}
                  </span>
                );
              })}
              {dungeonBiome.dungeonMobPool.length > 5 && (
                <span className="wm-dungeon-mob-count">+{dungeonBiome.dungeonMobPool.length - 5}</span>
              )}
            </div>
          </div>

          <div className="wm-dungeon-loot-compact">
            <span className="wm-dungeon-compact-label">Loot</span>
            <span className="wm-loot-tier-compact">
              {dungeonBiome.dungeonLootTable.charAt(0).toUpperCase() + dungeonBiome.dungeonLootTable.slice(1)}
            </span>
          </div>
        </div>
      )}

      {/* Normal Tile Info - Hide for dungeon/encounter/kingdom biomes AND outpost tiles */}
      {!isDungeonBiome && !isEncounterBiome && !isKingdomBiome && !hasOutpost && yields?.yields && Object.keys(yields.yields).length > 0 && (
        <div className="wm-preview-section">
          <div className="wm-preview-label">
            Gather
            {node.tileQuality && (
              <span className={`wm-tile-quality ${node.tileQuality >= 1.1 ? 'high' : node.tileQuality <= 0.9 ? 'low' : ''}`}>
                {Math.round(node.tileQuality * 100)}%
              </span>
            )}
          </div>
          <div className="wm-preview-yields">
            {Object.entries(yields.yields).map(([r, a]) => (
              <ResourceChip key={r} resourceId={r} amount={a} size="small" className="wm-gather-chip" />
            ))}
          </div>
        </div>
      )}
      {tileBuildingId && tileBuilding && !isKingdomBiome && !hasOutpost && (
        <div className="wm-preview-section">
          <div className="wm-preview-label">Built Here</div>
          <div className="wm-preview-building">
            <BuildingChip id={tileBuildingId} />
          </div>
        </div>
      )}

      {/* Can Build - show if no building exists (display "None" when empty) - Hide for dungeon/encounter/tower/kingdom/outpost/wonder sites */}
      {!tileBuildingId && !isDungeonBiome && !isEncounterBiome && !isTowerBiome && !node.isHub && !isKingdomBiome && !hasOutpost && !isWonderSite && (
        <CanBuildSection
          availableBuildings={availableBuildings}
          buildingCounts={buildingCounts}
        />
      )}

      {/* Trade Route Status - compact, only for regular tiles - Hide for dungeon/encounter/tower/kingdom/outpost/wonder sites */}
      {!node.isHub && !isDungeonBiome && !isEncounterBiome && !isTowerBiome && !isKingdomBiome && !hasOutpost && !isWonderSite && (
        <div className="wm-preview-section wm-tr-preview-compact">
          <div className="wm-preview-label">
            🛤️ Trade Route:{' '}
            {isOnTradeRoute ? (
              <span className="wm-tr-inline connected">✅ Connected</span>
            ) : hasTradeRouteSegment ? (
              <span className="wm-tr-inline partial">🔗 Not Connected</span>
            ) : (
              <span className="wm-tr-inline none">No Route</span>
            )}
          </div>
        </div>
      )}

      {/* Create Outpost Button - show for regular tiles without outposts - Hide for dungeon/encounter/tower/kingdom/wonder sites */}
      {!node.isHub && !isDungeonBiome && !isEncounterBiome && !isTowerBiome && !isKingdomBiome && !isWonderSite && !worldMap.outposts?.[previewTile] && onCreateOutpost && (
        <div className="wm-preview-section wm-outpost-section">
          <button
            className="wm-create-outpost-btn"
            onClick={() => onCreateOutpost(previewTile)}
          >
            🏰 Create Outpost
          </button>
        </div>
      )}

      {/* Outpost Info - show if outpost exists on this tile */}
      {worldMap.outposts?.[previewTile] && (() => {
        const outpost = worldMap.outposts[previewTile];
        const progress = outpost.isConstructing && getOutpostConstructionProgress
          ? getOutpostConstructionProgress(previewTile)
          : null;
        return (
          <div className="wm-preview-section wm-outpost-info">
            <div className="wm-preview-label">🏰 Outpost</div>
            <div className="wm-outpost-name">{outpost.name}</div>
            {outpost.isConstructing && progress ? (
              <div className="wm-outpost-construction">
                <div className="wm-outpost-progress-bar">
                  <div
                    className="wm-outpost-progress-fill"
                    style={{ width: `${progress.percent}%` }}
                  />
                </div>
                <div className="wm-outpost-timer">
                  ⏱️ {progress.formattedRemaining} remaining
                </div>
              </div>
            ) : outpost.isConstructing ? (
              <div className="wm-outpost-status constructing">Under Construction</div>
            ) : (
              <>
                {/* Trade Route Status for completed outposts */}
                <div className="wm-outpost-trade-route">
                  <span className="wm-outpost-tr-label">🛤️ Trade Route:</span>
                  {isOnTradeRoute ? (
                    <span className="wm-tr-inline connected">✅ Connected</span>
                  ) : hasTradeRouteSegment ? (
                    <span className="wm-tr-inline partial">🔗 Not Connected</span>
                  ) : (
                    <span className="wm-tr-inline none">No Route</span>
                  )}
                </div>
                {onManageOutpost && (
                  <button
                    className="wm-manage-outpost-btn"
                    onClick={() => onManageOutpost(previewTile)}
                  >
                    ⚔️ Manage Outpost
                  </button>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Great Work Under Construction */}
      {greatWorkInProgress && progressInfo && (
        <div className="wm-preview-section wm-preview-greatwork">
          <div className="wm-preview-label wm-gw-constructing">
            🔨 Building Great Work
          </div>
          <div className="wm-gw-info-panel">
            <span className="wm-gw-icon">{greatWorkInProgress.icon}</span>
            <span className="wm-gw-name">{greatWorkInProgress.name}</span>
          </div>
          <div className="wm-gw-progress-bar">
            <div
              className="wm-gw-progress-fill"
              style={{ width: `${progressInfo.percent}%` }}
            />
          </div>
          <div className="wm-gw-timer">
            {formatTime(progressInfo.remaining)} remaining ({Math.round(progressInfo.percent)}%)
          </div>
        </div>
      )}

      {/* Completed Great Work */}
      {builtGreatWork && !isGreatWorkUnderConstruction && (
        <div className="wm-preview-section wm-preview-greatwork wm-preview-greatwork-complete">
          <div className="wm-preview-label wm-gw-complete">
            ✨ Great Work
          </div>
          <div className="wm-gw-info-panel">
            <span className="wm-gw-icon">{builtGreatWork.icon}</span>
            <span className="wm-gw-name">{builtGreatWork.name}</span>
          </div>
          {builtGreatWork.effects && (
            <div className="wm-gw-effects">
              {builtGreatWork.effects.productionBonus && Object.entries(builtGreatWork.effects.productionBonus).map(([res, val]) => (
                <span key={res} className="wm-gw-effect bonus">
                  {getResourceIcon(res)} {formatEffect(val)}
                </span>
              ))}
              {builtGreatWork.effects.globalProductionBonus && (
                <span className="wm-gw-effect global">
                  🌍 All: {formatEffect(builtGreatWork.effects.globalProductionBonus)}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TilePreviewPanel;
